import { qk, useCustomerProfile } from '@restaurant/api-client/react';
import { DIET_RESTRICTION_LABELS, DIET_RESTRICTIONS, formatMoney, type CustomerDto, type DietRestriction } from '@restaurant/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Leaf, ThumbsUp, Repeat } from 'lucide-react';
import { useState } from 'react';
import { Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { cn } from '@/lib/utils';

/** Diet (told us + likely from orders), usual dishes and diet-safe recommendations for the chat's customer. */
export function CustomerInsights({ customer, currency }: { customer: CustomerDto; currency: string }) {
  const profile = useCustomerProfile(customer.id);
  const qc = useQueryClient();
  const [saving, setSaving] = useState<DietRestriction | null>(null);
  const p = profile.data;

  const toggle = async (r: DietRestriction) => {
    const next = customer.dietRestrictions.includes(r) ? customer.dietRestrictions.filter((x) => x !== r) : [...customer.dietRestrictions, r];
    setSaving(r);
    try {
      await api.customers.update(customer.id, { dietRestrictions: next });
      await qc.invalidateQueries({ queryKey: qk.customerProfile(customer.id) });
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(null);
    }
  };

  return (
    <section className="space-y-3">
      <div>
        <p className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold"><Leaf className="size-4 text-primary" /> Diet</p>
        <div className="flex flex-wrap gap-1.5">
          {DIET_RESTRICTIONS.map((r) => {
            const told = customer.dietRestrictions.includes(r);
            const likely = !told && p?.diet.inferred.includes(r);
            return (
              <button
                key={r}
                onClick={() => void toggle(r)}
                disabled={saving !== null}
                title={told ? 'Customer told us (click to remove)' : likely ? 'Likely, from past orders (click to confirm)' : 'Click if the customer told you'}
                className={cn(
                  'flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
                  told ? 'border-primary bg-primary text-primary-foreground' : likely ? 'border-dashed border-primary text-primary' : 'text-muted-foreground hover:bg-accent',
                )}
              >
                {saving === r ? <Spinner /> : null}
                {DIET_RESTRICTION_LABELS[r]}
                {likely ? ' (likely)' : ''}
              </button>
            );
          })}
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">Recommendations and AI replies never suggest dishes that conflict with these.</p>
      </div>

      {profile.isLoading ? <Spinner /> : null}
      {p?.usual.length ? (
        <div>
          <p className="mb-1 flex items-center gap-1.5 text-sm font-semibold"><Repeat className="size-4 text-primary" /> Usually orders</p>
          <ul className="space-y-0.5 text-sm">
            {p.usual.map((u) => (
              <li key={u.name} className={cn('flex justify-between gap-2', !u.available && 'text-muted-foreground line-through')}>
                <span className="truncate">{u.name}</span>
                <span className="tabular-nums text-muted-foreground">×{u.quantity}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {p?.recommendations.length ? (
        <div>
          <p className="mb-1 flex items-center gap-1.5 text-sm font-semibold"><ThumbsUp className="size-4 text-primary" /> Might like</p>
          <ul className="space-y-1 text-sm">
            {p.recommendations.map((r) => (
              <li key={r.menuItemId}>
                <div className="flex justify-between gap-2">
                  <span className="truncate">{r.name}</span>
                  <span className="tabular-nums text-muted-foreground">{formatMoney(r.priceCents, currency)}</span>
                </div>
                <p className="text-[11px] text-muted-foreground">{r.reason}</p>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {p && p.orderCount > 0 ? (
        <p className="text-xs text-muted-foreground">
          {p.orderCount} order{p.orderCount === 1 ? '' : 's'} · {formatMoney(p.totalSpentCents, currency)} spent
        </p>
      ) : null}
    </section>
  );
}
